/**
 * @file authProvider.tsx
 * @description This file defines the authentication context provider for the application.
 * It manages both MSAL (Microsoft Single Sign-On) and local (username/password) authentication states,
 * providing a unified `useAuth` hook for components to access authentication status, user information,
 * and login/logout functions.
 */

import React, { createContext, useState, useEffect, useContext, useRef } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { loginLocalApi, getUsername, getToken as getMsalToken, verifyMfaApi } from "../api";

/**
 * @interface AuthContextType
 * @description Defines the shape of the authentication context provided to the application.
 */
export interface AuthContextType {
    /** A boolean indicating if the user is authenticated via any method (MSAL or local). */
    isAuthenticated: boolean;
    /** A boolean indicating if the current authentication is local (username/password). */
    isLocalAuth: boolean;
    /** A boolean indicating if the current authentication is from a social provider like Google. */
    isSocialAuth: boolean;
    /** A boolean indicating if the authentication state is currently being determined. */
    isLoading: boolean;
    /** The JWT for local authentication, if available. */
    localToken: string | null;
    /** The name of the currently authenticated user. */
    username: string;
    /** Function to initiate login, supporting both 'msal' and 'local' types. */
    login: (
        loginType: "msal" | "local" | "mfa" | "token",
        loginRequest?: any
    ) => Promise<{ mfa_required?: boolean; mfa_setup_required?: boolean; username?: string; setup_token?: string } | void>;
    /** Function to log the user out. */
    logout: () => void;
    /** Asynchronously retrieves the authentication token (either local JWT or MSAL token). */
    getAuthToken: () => Promise<string | null>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface Props {
    children?: React.ReactNode;
}

/**
 * @component AuthProvider
 * @description A React component that provides authentication context to its children.
 * It handles the logic for both MSAL and local authentication, synchronizing state
 * with `localStorage` and the MSAL instance.
 */
export const AuthProvider: React.FC<Props> = ({ children }) => {
    // MSAL hooks for instance and authentication status.
    const { instance, accounts } = useMsal();
    const msalIsAuthenticated = useIsAuthenticated();

    // State for local authentication (username/password).
    const [isLocalAuth, setIsLocalAuth] = useState<boolean>(false);
    // State for social authentication (e.g., Google).
    const [isSocialAuth, setIsSocialAuth] = useState<boolean>(false);

    // Initialize local token from localStorage.
    const [localToken, setLocalToken] = useState<string | null>(() => localStorage.getItem("localToken"));

    // The primary authentication flag, true if either MSAL or local auth is active.
    const isAuthenticated = msalIsAuthenticated || !!localToken;

    // State for the user's name and the initial loading status.
    const [username, setUsername] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(true);
    // Ref to prevent initializeAuth from resetting isLoading=false while logout is in progress.
    const isLoggingOutRef = useRef(false);

    /**
     * @hook useEffect
     * @description This effect runs on mount and whenever MSAL's state changes. It initializes
     * the authentication state by checking for a valid local token in localStorage or by
     * setting the active MSAL account and fetching the username.
     */
    useEffect(() => {
        console.log(`[AuthProvider] Effect fired. msalIsAuthenticated: ${msalIsAuthenticated}, accounts.length: ${accounts.length}`);

        const initializeAuth = async () => {
            try {
                const storedToken = localStorage.getItem("localToken");
                if (storedToken) {
                    // If a local token exists, validate its expiration.
                    const payload = JSON.parse(atob(storedToken.split(".")[1]));
                    if (payload.exp * 1000 > Date.now()) {
                        // Token is valid. Check if it's a social login token.
                        // We can add a claim like 'iss' (issuer) in the backend to differentiate.
                        // For now, we'll assume if it's not MSAL, it could be local or social.
                        // A simple check could be if the username is an email for social logins.
                        if (payload.iss === "google") {
                            setIsSocialAuth(true);
                        } else {
                            setIsLocalAuth(true);
                        }
                        setLocalToken(storedToken);
                        setUsername(payload.name || "Local User");
                    } else {
                        // Token is expired, remove it.
                        localStorage.removeItem("localToken");
                    }
                } else if (msalIsAuthenticated && accounts.length > 0) {
                    // If no local token, but MSAL is authenticated, set up MSAL user.
                    const account = instance.getActiveAccount() || accounts[0];
                    instance.setActiveAccount(account);
                    const name = await getUsername(instance);
                    setUsername(name || account.username || "");
                }
            } catch (e) {
                console.error("Error during auth initialization:", e);
                // Ensure loading is always set to false, even on error.
            }
            // Do not reset isLoading while a logout is in progress — the logout
            // itself manages the loading state (and the browser is about to navigate away).
            if (!isLoggingOutRef.current) {
                setIsLoading(false);
            }
        };

        initializeAuth();
    }, [instance, msalIsAuthenticated, accounts]);

    /**
     * @function login
     * @description Handles the login process for both MSAL and local authentication.
     * @param {'msal' | 'local'} loginType - The type of login to perform.
     * @param {any} [localLoginRequest] - The credentials for a local login.
     */
    const login = async (loginType: "msal" | "local" | "mfa" | "token", loginRequest?: any) => {
        if (loginType === "msal") {
            await instance.loginRedirect();
        } else if (loginType === "local") {
            if (!loginRequest) {
                throw new Error("Local login request is missing.");
            }
            const response = await loginLocalApi(loginRequest.username, loginRequest.password);

            if (response.mfa_required) {
                return { mfa_required: true, username: response.username };
            } else if (response.mfa_setup_required) {
                return { mfa_setup_required: true, setup_token: response.setup_token };
            } else if (response.token) {
                localStorage.setItem("localToken", response.token);
                setLocalToken(response.token);
                setIsLocalAuth(true);
                setUsername(loginRequest.username);
            }
        } else if (loginType === "mfa") {
            const response = await verifyMfaApi(loginRequest.username, loginRequest.totp_code);
            localStorage.setItem("localToken", response.token);
            setLocalToken(response.token);
            setIsLocalAuth(true);
            setUsername(loginRequest.username);
        } else if (loginType === "token") {
            // Used after mandatory MFA setup to finalize the session
            const token = loginRequest.token;
            const payload = JSON.parse(atob(token.split(".")[1]));
            localStorage.setItem("localToken", token);
            setLocalToken(token);
            // Differentiate between a local user completing MFA and a Google user.
            // A simple way is to check the token issuer if you add it on the backend.
            if (payload.iss === "google") {
                setIsSocialAuth(true);
            } else {
                setIsLocalAuth(true);
            }
            setUsername(payload.name || "Local User");
        }
    };

    /**
     * @function logout
            setLocalToken(response.token);
            setIsLocalAuth(true);
            setUsername(loginRequest.username);
        }
    };

    /**
     * @function logout
     * @description Handles the logout process, clearing local storage for local auth
     * or triggering a redirect for MSAL auth.
     */
    const logout = async () => {
        // Prevent the MSAL-state-change useEffect from calling setIsLoading(false)
        // while we are in the middle of logging out (browser redirect in progress).
        isLoggingOutRef.current = true;
        setIsLoading(true);
        if (isLocalAuth || isSocialAuth) {
            localStorage.removeItem("localToken");
            setLocalToken(null);
            setIsLocalAuth(false);
            setIsSocialAuth(false);
            setUsername("");
            isLoggingOutRef.current = false;
            setIsLoading(false);
        } else {
            // For MSAL, let the browser navigate away to the Azure AD logout page.
            // isLoggingOutRef=true already prevents the useEffect from calling setIsLoading(false)
            // before the redirect happens, avoiding a flash of the login screen.
            await instance.logoutRedirect();
        }
    };

    /**
     * @function getAuthToken
     * @description A unified method to retrieve the current authentication token,
     * prioritizing the local token if it exists, otherwise falling back to the MSAL token.
     * @returns {Promise<string | null>} The authentication token or null if not available.
     */
    const getAuthToken = async (): Promise<string | null> => {
        // Check for local token first (checks the actual token, not the flag)
        if (localToken) {
            // Verify the token hasn't expired while the tab was open
            try {
                const payload = JSON.parse(atob(localToken.split(".")[1]));
                if (payload.exp * 1000 <= Date.now()) {
                    // Token expired — clear session and force re-login
                    localStorage.removeItem("localToken");
                    setLocalToken(null);
                    setIsLocalAuth(false);
                    setIsSocialAuth(false);
                    setUsername("");
                    return null;
                }
            } catch {
                // Malformed token — clear it
                localStorage.removeItem("localToken");
                setLocalToken(null);
                setIsLocalAuth(false);
                setIsSocialAuth(false);
                setUsername("");
                return null;
            }
            return localToken;
        }
        // Fallback to MSAL token if no local token
        const token = getMsalToken(instance);
        return token ?? null;
    };

    // The value provided to all consumers of the AuthContext.
    const authContextValue = {
        isAuthenticated,
        isLocalAuth,
        isSocialAuth,
        isLoading,
        localToken,
        username,
        login,
        logout,
        getAuthToken
    };

    return <AuthContext.Provider value={authContextValue}>{children}</AuthContext.Provider>;
};

/**
 * @component StaticAuthProvider
 * @description A lightweight auth provider for static deployments where authentication is disabled.
 * It avoids MSAL dependencies and exposes a safe, non-authenticated context.
 */
export const StaticAuthProvider: React.FC<Props> = ({ children }) => {
    const authContextValue: AuthContextType = {
        isAuthenticated: false,
        isLocalAuth: false,
        isSocialAuth: false,
        isLoading: false,
        localToken: null,
        username: "",
        login: async () => {},
        logout: () => {},
        getAuthToken: async () => null
    };

    return <AuthContext.Provider value={authContextValue}>{children}</AuthContext.Provider>;
};

/**
 * @hook useAuth
 * @description A custom hook to easily consume the `AuthContext`.
 * @throws {Error} If used outside of an `AuthProvider`.
 */
export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};
